import { useDispatch } from 'react-redux'
import { Button, ButtonProps, Dialog, DialogContent, DialogTitle } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { hideLift, showLift } from '../redux/slices/lifts'
import { useTranslation } from 'react-i18next'
import { Lift, Project } from '../types'
import { useState } from 'react'
import LiftForm from './LiftForm'
import { usePolicy } from '../hooks'
import { ProjectStateType } from '../enums/ProjectStateType'

export interface LiftCreateButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  project?: Project
  afterSubmit?: (lift: Lift) => void
}

export default function LiftCreateButton({
  project,
  afterSubmit,
  ...props
}: LiftCreateButtonProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * Indicates if the dialog is open.
   */
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  /**
   * Open the dialog.
   */
  function openDialog(): void {
    dispatch(showLift(undefined))
    setIsDialogOpen(true)
  }

  /**
   * Close the dialog.
   */
  function closeDialog(): void {
    setIsDialogOpen(false)
    dispatch(hideLift())
  }

  function handleAfterSubmit(lift: Lift): void {
    afterSubmit?.(lift)
    closeDialog()
  }

  return (
    <>
      {project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project) && (
        <Button
          variant="contained"
          color="primary"
          endIcon={<AddIcon fontSize="inherit" />}
          {...props}
          onClick={() => openDialog()}
        >
          {t('newLift')}
        </Button>
      )}

      <Dialog open={isDialogOpen} maxWidth="sm" fullWidth onClose={() => closeDialog()}>
        <DialogTitle>{t('newLift')}</DialogTitle>
        <DialogContent>
          <LiftForm
            value={{ project: project?.id }}
            onCancel={() => closeDialog()}
            afterSubmit={handleAfterSubmit}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
